import { BenchmarkCategory } from '@/api/generated-catch-backend/model';
import MarkdownTextTailwind from '@/components/markdown/markdown-text-tailwind';
import useAnalyticsCalls from '@/hooks/use-analytics';
import { motion } from 'motion/react';

interface HeaderTitleProps {
  name?: string;
  benchmark?: BenchmarkCategory;
  className?: string;
  methodologyUrl?: string;
  lifetimeRisk?: number;
  populationAverage?: number;
  userGender?: 'male' | 'female' | null;
  biologicalAgeText?: string;
}

export default function HeaderTitle({
  name,
  benchmark,
  className = '',
  methodologyUrl,
  lifetimeRisk,
  populationAverage,
  userGender,
  biologicalAgeText,
}: HeaderTitleProps) {
  const { callLinkClicked } = useAnalyticsCalls();

  const titleContent = name && benchmark ? `Hi ${name}, your overall risk is **${benchmark.toLowerCase()}**` : '';
  const genderText = userGender === 'male' ? 'men' : 'women';
  const subtitleContent =
    lifetimeRisk && populationAverage && userGender
      ? `Your estimated lifetime risk of developing cancer is **${lifetimeRisk}%**, compared to the U.S. average of **${populationAverage}%** for ${genderText} your age.\n\n${biologicalAgeText}`
      : '';
  return (
    <motion.div
      className={`flex hidden h-full w-full flex-col items-start text-left md:flex ${className}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
    >
      <div className="flex h-full w-full flex-1 flex-col justify-between py-4">
        <motion.h1
          className="text-left text-4xl leading-tight font-normal text-white md:text-5xl"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <MarkdownTextTailwind>{titleContent}</MarkdownTextTailwind>
        </motion.h1>

        {/* Divider */}
        <motion.div
          className="h-px w-full bg-white/20"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        />

        {subtitleContent && (
          <motion.div
            className="text-left text-base leading-relaxed text-white/80 md:text-lg"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <MarkdownTextTailwind>{subtitleContent}</MarkdownTextTailwind>
          </motion.div>
        )}

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5, delay: 0.6 }}>
          <a
            href={methodologyUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="focus:ring-opacity-50 cursor-pointer rounded-sm text-[16px] leading-[1.3] font-semibold text-[#7cf1c7] underline transition-colors hover:text-white focus:ring-2 focus:ring-[#7cf1c7] focus:outline-none"
            aria-label="Learn how your cancer risk is calculated (opens in new tab)"
            onClick={() => callLinkClicked('Learn how your risk is calculated', methodologyUrl || '')}
          >
            Learn how your risk is calculated
          </a>
        </motion.div>
      </div>
    </motion.div>
  );
}
